import { APP_NAME } from '@eventping/shared'
import { useMutation } from '@tanstack/react-query'
import { MailX } from 'lucide-react'
import { useEffect } from 'react'
import { Card, Footer, Page, Wordmark } from '@/components/shell'
import { api, useTexts } from '@/lib/api'

/**
 * /u/<token>, the unsubscribe link in every email: one click. Opening the page unsubscribes
 * (a POST, so link scanners that only GET change nothing). Mail apps use the
 * List-Unsubscribe-Post header, which POSTs to /api/u/<token> directly.
 */
export function UnsubscribePage({ token }: { token: string }) {
  const unsubscribe = useMutation({
    mutationFn: () => api<{ event: string }>(`/api/u/${token}`, { method: 'POST' }),
  })
  const { mutate } = unsubscribe
  const texts = useTexts()
  useEffect(() => mutate(), [mutate])

  return (
    <Page header={<Wordmark />}>
      <Card className="text-center">
        <MailX className="mx-auto mb-3 size-10 text-muted-foreground" aria-hidden />
        {unsubscribe.isSuccess ? (
          <>
            <h1 className="text-xl font-semibold">You’re unsubscribed</h1>
            <p className="mt-2 text-muted-foreground">
              You won’t get any more {APP_NAME} emails about {unsubscribe.data.event}.
              {texts && ' If you also get texts about it, reply STOP to any of them to stop those.'}
            </p>
          </>
        ) : unsubscribe.isError ? (
          <>
            <h1 className="text-xl font-semibold">This unsubscribe link isn’t valid</h1>
            <p className="mt-2 text-muted-foreground">
              Use the link from the most recent email, or write to us at the address below.
            </p>
          </>
        ) : (
          <h1 className="text-xl font-semibold">Unsubscribing…</h1>
        )}
      </Card>
      <Footer />
    </Page>
  )
}
